import { Button } from '@mui/material'
import { lazy, Suspense, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import * as v from 'valibot'
import { MediaStreamPlayer } from 'src/components/Media/MediaStreamPlayer'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { executeUpdateMyInfo } from 'src/services/apollo/auth'
import type { User } from 'src/services/apollo/generated/graphql'
import { DecorationType } from 'src/services/apollo/generated/graphql'
import { useCampaignAds } from 'src/services/orpc/campaignAds'
import { cx } from 'src/utils/cx'
import { getPublicUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import { uploadMedia } from 'src/utils/upload'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { userHasMembership } from 'src/utils/user'
import { DecorationLiveBannerDataSchema } from '~/features/UserDecoration/schema/decoration'
import {
  getUserActiveDecoration,
  useToggleMyActiveDecoration,
} from '~/features/UserDecoration/utils/activeDecoration'
import { useMyActiveDecorations } from '~/services/orpc/decoration'
import type { CropImageRef } from '../CropImage'
import LoadingPage from '../LoadingPage'
import { UIButton } from '../ui/Button'

const CropImage = lazy(() => import('src/components/CropImage'))

type LiveBannerData = v.InferOutput<typeof DecorationLiveBannerDataSchema>

export function ProfileCover(
  props: StyleProps & {
    user?: User
    isMe?: boolean
    allowEdit?: boolean
    /**
     * no effect unless allowEdit is true
     */
    hoverToEdit?: boolean
  },
) {
  const { user, isMe, allowEdit, hoverToEdit, ...restProps } = props
  const coverImage = getPublicUrl(user?.coverMedia)

  const isMember = userHasMembership(user)

  const { data: myActiveDecorations } = useMyActiveDecorations({
    enabled: !!isMe,
  })

  const userLiveBannerData = useMemo(() => {
    const liveBannerData = isMe
      ? myActiveDecorations?.find(
          it => it.decoration.type === DecorationType.LiveBanner,
        )?.decoration.data
      : getUserActiveDecoration(user, { type: DecorationType.LiveBanner })
          ?.decoration.data

    const { success, output } = v.safeParse(
      DecorationLiveBannerDataSchema,
      liveBannerData,
    )
    if (!success) return undefined
    return output
  }, [isMe, myActiveDecorations, user?.activeDecorations])

  const hasLiveBanner = !!userLiveBannerData?.mediaSpec
  // Ignore custom cover image for non-members
  const hasCoverImage = isMember && !!coverImage

  if (!isMe && !hasLiveBanner && !hasCoverImage) {
    return null
  }

  return (
    <ProfileCoverInner {...restProps}>
      <ProfileCoverBackground
        liveBannerData={userLiveBannerData}
        coverImage={coverImage}
        isMember={isMember}
      />
      <ProfileCoverPromo isMe={isMe} isMember={isMember} />
      <ProfileCoverEditButton
        isMe={isMe}
        isMember={isMember}
        allowEdit={allowEdit}
        hoverToEdit={hoverToEdit}
        hasCoverImage={hasCoverImage}
      />
    </ProfileCoverInner>
  )
}

function ProfileCoverBackground(props: {
  liveBannerData?: LiveBannerData
  coverImage?: string
  isMember: boolean
}) {
  const { liveBannerData, coverImage, isMember } = props

  const { i18n } = useTranslation('membership')
  const { data: campaignData } = useCampaignAds({
    lang: i18n.language,
    category: 'profilePromotionalBanner',
  })
  const campaignConfig = campaignData?.[0]

  const promotionalCover = campaignConfig?.media

  if (liveBannerData?.mediaSpec) {
    return (
      <MediaStreamPlayer
        mediaSpec={liveBannerData.mediaSpec}
        mediaType={liveBannerData.mediaType}
        className="absolute object-cover w-full h-full mmd:rounded-t-lg"
      />
    )
  }

  const backgroundImage =
    (isMember && coverImage) || (!isMember && promotionalCover) || undefined

  if (backgroundImage) {
    return (
      <img
        className="absolute object-cover w-full h-full mmd:rounded-t-lg"
        src={backgroundImage}
        alt="cover image"
        loading="lazy"
        decoding="async"
      />
    )
  }

  return null
}

function ProfileCoverPromo(props: { isMe?: boolean; isMember: boolean }) {
  const { t, i18n } = useTranslation('membership')
  const { isMe, isMember } = props

  const { data: campaignData } = useCampaignAds({
    lang: i18n.language,
    category: 'profilePromotionalBanner',
  })

  if (!isMe || isMember) return null

  const promotionalLink = campaignData?.[0]?.targetUrl ?? '/membership/plans'
  const promotionalText = t('profile-page.promotion-banner.text')

  return (
    <>
      {promotionalText && (
        <div className="absolute w-full h-full bg-[#c7c7c7]/50 dark:bg-background/40" />
      )}
      {promotionalText && (
        <span
          className="absolute right-2 bottom-2 text-sm md:text-base mlg:text-xl text-white"
          style={{
            textShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            WebkitTextStroke: '1',
            WebkitTextStrokeColor: 'white',
          }}
        >
          {promotionalText}
        </span>
      )}
      <Link
        to={promotionalLink}
        className="absolute w-full h-full cursor-pointer"
      />
    </>
  )
}

function ProfileCoverEditButton(props: {
  isMe?: boolean
  isMember: boolean
  allowEdit?: boolean
  hoverToEdit?: boolean
  hasCoverImage: boolean
}) {
  const { t } = useTranslation(['user'])
  const { isMe, isMember, allowEdit, hoverToEdit, hasCoverImage } = props
  const [openDialog, setOpenDialog] = useState(false)

  if (!isMe || !isMember || !allowEdit) return null

  return (
    <>
      <Button
        variant={hasCoverImage ? 'contained' : 'outlined'}
        className={cx(
          'rounded-md',
          hasCoverImage ? 'text-black bg-white' : 'text-white border-white',
          hoverToEdit && 'invisible group-hover:visible',
        )}
        onClick={() => {
          track('banner_change_click')
          setOpenDialog(true)
        }}
      >
        {t('user:profile-edit.change-banner.label')}
      </Button>
      <ProfileCoverEditDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      />
    </>
  )
}

function ProfileCoverEditDialog(props: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation(['user'])

  const cropImageRef = useRef<CropImageRef>(null)
  const [hasImage, setHasImage] = useState(false)

  const toggleOffLiveBanner = useToggleMyActiveDecoration({
    type: DecorationType.LiveBanner,
    target: false,
  })

  const handleClose = () => {
    setHasImage(false)
    cropImageRef.current?.reset()
    props.onClose()
  }

  const [loading, submit] = useLoadingCallback(async () => {
    if (!cropImageRef.current) return
    track('banner_submit_click', {
      size: cropImageRef.current.zoom,
    })
    const file = await cropImageRef.current.getImage()
    if (!file) return
    try {
      const media = await uploadMedia(file, { preserveFileType: true })
      if (!media) return
      await executeUpdateMyInfo({
        input: {
          coverMediaId: media.mediaId,
        },
      })

      /** Ensure the new banner not being overridden by the live banner decoration */
      toggleOffLiveBanner()

      toast.success(`Success`)
      handleClose()
    } catch (err) {
      toast.error(
        `Failed to upload image: ${err instanceof Error ? err.message : String(err)}`,
      )
    }
  }, [])

  const allowedFileTypes = ['image/jpeg', 'image/png'].join(',')

  return (
    <UIDialog
      open={props.open}
      onClose={handleClose}
      dismissable
      className="w-[calc(100vw-2rem)] max-w-[1600px]"
    >
      <UIDialogHeader heading={t('user:profile-edit.change-banner.label')}>
        {({ heading }) => (
          <div className="prose dark:prose-invert">{heading}</div>
        )}
      </UIDialogHeader>
      <UIDialogContent>
        <Suspense fallback={<LoadingPage />}>
          <CropImage
            ref={cropImageRef}
            allowedFileTypes={allowedFileTypes}
            maxWidth={1500}
            maxHeight={500}
            onImageChange={setHasImage}
          />
        </Suspense>
      </UIDialogContent>
      <UIDialogFooter>
        {hasImage && (
          <UIButton
            variant="outlined"
            onPress={() => cropImageRef.current?.reset()}
          >
            {t('action:back')}
          </UIButton>
        )}
        <div className="flex-1" />
        <UIButton variant="outlined" onPress={handleClose}>
          {t('action:cancel')}
        </UIButton>
        {hasImage && (
          <UIButton variant="filled" busy={loading} onPress={submit}>
            {t('user:submit-avatar')}
          </UIButton>
        )}
      </UIDialogFooter>
    </UIDialog>
  )
}

function ProfileCoverInner(props: {
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div
      className={cx(
        'flex items-center justify-center relative group',
        'aspect-[3] mmd:aspect-[5]',
        'bg-zinc-700',
        props.className,
      )}
      style={props.style}
    >
      {props.children}
    </div>
  )
}
